import { change, initialize } from 'redux-form';
import { BENE_PERSONAL_DETAILS_FORM_NAME } from '../../../constants/commons';
import { REMITTER_PERSONAL_FIELDS_ARRAY } from './constants';
import { getPhoneValidationsByCountry } from '@Redux/actions/otp-request';
import { CORPORATE } from '@Utils/constants';
import { setIsSelfBene } from '@Redux/actions/new-bene-flow/new-bene-flow-action';
import { AppDispatch } from '../../../../../store.type';
import { CountryList, GenericRecordType } from './types';
import { SELF_POPULATING_FIELDS } from '../../../../beneficiary-info/constant';
import { email, required } from '@Utils/validation';

export const selfPopulatingDataNonBeneSchema = (
  isRelationSelf: boolean,
  dispatch: AppDispatch,
  combinedFormValues: GenericRecordType,
  countryDropdown: CountryList,
  userStatusReducer: GenericRecordType,
  setMobileValidation: (data: null | GenericRecordType) => any,
  account_type: string,
  mobCode: string
) => {
  const { bank_account_country } = combinedFormValues;

  const addressRequired = countryDropdown.some(
    (country) => country.key === userStatusReducer.country_code
  );
  !addressRequired &&
    REMITTER_PERSONAL_FIELDS_ARRAY.splice(
      REMITTER_PERSONAL_FIELDS_ARRAY.indexOf('country_code'),
      1
    );
  ['KE'].includes(bank_account_country) &&
    REMITTER_PERSONAL_FIELDS_ARRAY.splice(
      REMITTER_PERSONAL_FIELDS_ARRAY.indexOf('mobile_country_code'),
      1
    );

  if (isRelationSelf) {
    dispatch(setIsSelfBene(true));
    const selfPopulatedValues = REMITTER_PERSONAL_FIELDS_ARRAY.reduce<Record<string, any>>(
      (acc, val) => {
        acc[val] =
          val === 'country_code'
            ? bank_account_country
            : val === 'email'
            ? userStatusReducer[val]
            : val === 'beneficiary_name'
            ? userStatusReducer['remitter_individual_full_name']
            : userStatusReducer[
                'remitter_individual_'.concat(val) as keyof typeof userStatusReducer
              ];
        return acc;
      },
      {}
    );
    dispatch(
      initialize(
        BENE_PERSONAL_DETAILS_FORM_NAME,
        { ...selfPopulatedValues, country_code: bank_account_country },
        false
      )
    );

    if (REMITTER_PERSONAL_FIELDS_ARRAY.includes('mobile_country_code')) {
      dispatch(getPhoneValidationsByCountry(userStatusReducer['country_code']));
      setMobileValidation(null);
    }
  } else if (!isRelationSelf) {
    dispatch(setIsSelfBene(false));
    const resetValues = REMITTER_PERSONAL_FIELDS_ARRAY.reduce<Record<string, string>>(
      (acc, val) => {
        if (val === 'country_code') {
          // Keep destination country — only name/mobile/email are cleared on non-self switch
          acc[val] = bank_account_country;
        } else if (
          account_type === CORPORATE &&
          ['company_name', 'mobile_number', 'email'].includes(val)
        ) {
          acc[val] = combinedFormValues[val];
        } else {
          acc[val] = '';
        }
        return acc;
      },
      {}
    );

    dispatch(
      initialize(
        BENE_PERSONAL_DETAILS_FORM_NAME,
        { ...resetValues, country_code: bank_account_country },
        false
      )
    );
    // Getting mobile code as per the bank account country
    dispatch(change(BENE_PERSONAL_DETAILS_FORM_NAME, 'mobile_country_code', mobCode.substring(1)));
    dispatch(getPhoneValidationsByCountry(bank_account_country));
    setMobileValidation(null);
  }
};

export const selfPopulatingDataBeneSchemaFields = (
  data: GenericRecordType,
  dispatch: AppDispatch,
  schemaApiResponse: GenericRecordType,
  userStatusReducer: GenericRecordType,
  beneCountryFieldDisabled: boolean,
  beneIdTypeFieldDisabled: boolean,
  bankAccountCountry: string
) => {
  const { personal_details } = schemaApiResponse || {};
  if (data.text === 'Self') {
    dispatch(setIsSelfBene(true));
    const selfPopulatedValues = Object.keys(SELF_POPULATING_FIELDS).reduce<Record<string, any>>(
      (acc, val) => {
        if (Object.keys(personal_details).includes(val)) {
          // Skip beneficiary_country_code if disabled
          if (
            (val === 'beneficiary_country_code' && beneCountryFieldDisabled) ||
            (val === 'beneficiary_identification_type' && beneIdTypeFieldDisabled)
          ) {
            return acc;
          }

          if (val === 'beneficiary_country_code') {
            acc[val] = bankAccountCountry;
            return acc;
          }

          const value =
            userStatusReducer[
              SELF_POPULATING_FIELDS[
                val as keyof typeof SELF_POPULATING_FIELDS
              ] as keyof typeof userStatusReducer
            ];

          acc[val] = value;
        }
        return acc;
      },
      {}
    );

    // Dispatch change for each field instead of initialize, initialize overwrites const values
    Object.entries(selfPopulatedValues).forEach(([field, value]) => {
      dispatch(change(BENE_PERSONAL_DETAILS_FORM_NAME, field, value));
    });
  } else {
    dispatch(setIsSelfBene(false));
    const fieldsToClear = Object.keys(SELF_POPULATING_FIELDS).reduce<Record<string, string>>(
      (acc, val) => {
        if (Object.keys(personal_details).includes(val)) {
          if (val === 'beneficiary_country_code' && beneCountryFieldDisabled) {
            return acc;
          }
          acc[val] = '';
        }
        return acc;
      },
      {}
    );

    const fieldsToDispatch = !beneCountryFieldDisabled
      ? { ...fieldsToClear, beneficiary_country_code: bankAccountCountry }
      : fieldsToClear;
    Object.entries(fieldsToDispatch).forEach(([field, value]) => {
      dispatch(change(BENE_PERSONAL_DETAILS_FORM_NAME, field, value));
    });
  }
};

export const toValidateEmail = (
  remiter_country: string,
  country_code: string,
  bank_account_country: string,
  currency_code: string,
  isWallet: boolean,
  isIndividualChinaBeneWithCNYCurrency: boolean,
  isPhpCurrency: boolean
) => {
  let emailProps = {
    validate: [required, email]
  };

  if ((bank_account_country === 'US' && currency_code === 'USD') || isWallet) {
    return (emailProps = {
      validate: [email]
    });
  }

  if (remiter_country === 'US' && (country_code === 'KR' || country_code === 'CO')) {
    return (emailProps = {
      validate: [required, email]
    });
  } else if (remiter_country === 'US' && !isIndividualChinaBeneWithCNYCurrency && !isPhpCurrency) {
    return (emailProps = {
      validate: [email]
    });
  }
  return emailProps;
};
